import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiErrorResponse } from "../models/ApiResponse";
import { BatchStaleness } from "../models/BatchStaleness";
import { NewSalaryBatchRequest, SalaryBatch, UpdateSalaryBatchConsultant } from "../models/Salary";
import { apiStore } from "../stores/api.store";

/**
 * Coerce the API's date strings to Date instances (the batch form and journal
 * work with Dates). Was SalaryStore.setSalaryBatchToEdit.
 */
export function parseSalaryBatch(salaryBatch: SalaryBatch): SalaryBatch {
	return {
		...salaryBatch,
		from: new Date(salaryBatch.from),
		to: new Date(salaryBatch.to),
	};
}

export function useSalaryBatches() {
	const { data, ...rest } = useQuery(["salary-batches"], () =>
		apiStore
			.get<{ salaryBatches: SalaryBatch[] }>({
				path: `/salary-batch`,
				isAuthRequired: true,
			})
			.then((d) => d.data.salaryBatches)
	);

	return {
		salaryBatches: data ?? [],
		...rest,
	};
}

export function usePollSalaryBatch() {
	// Note: mutation is better than query for polling
	return useMutation({
		mutationFn: (id: string) =>
			apiStore
				.get<SalaryBatch>({
					path: `/salary-batch/${id}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
	});
}

export function useCreateSalaryBatch() {
	const queryClient = useQueryClient();

	return useMutation<SalaryBatch, ApiErrorResponse, NewSalaryBatchRequest>(
		(body) =>
			apiStore
				.post({
					path: `/salary-batch`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["salary-batches"]);
			},
		}
	);
}

/** Set which consultants are included in the batch; returns the updated batch. */
export function useUpdateSalaryBatchConsultants() {
	return useMutation<
		SalaryBatch,
		ApiErrorResponse,
		{ salaryBatchId: string; update: UpdateSalaryBatchConsultant }
	>((params) =>
		apiStore
			.put({
				path: `/salary-batch/${params.salaryBatchId}/consultant`,
				body: params.update,
				isAuthRequired: true,
			})
			.then((d) => d.data)
	);
}

export function useProcessSalaryBatch() {
	const queryClient = useQueryClient();

	return useMutation<SalaryBatch, ApiErrorResponse, string>(
		(salaryBatchId) =>
			apiStore
				.post({
					path: `/salary-batch/${salaryBatchId}/process`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["salary-batches"]);
			},
		}
	);
}

export function useRollbackSalaryBatch() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, string>(
		(salaryBatchId) =>
			apiStore
				.post({
					path: `/salary-batch/${salaryBatchId}/rollback`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["salary-batches"]);
			},
		}
	);
}

/**
 * Rebuild a draft (PREVIEW or FAILED) batch's preview in place. The backend
 * answers with the batch reset to INITIAL — poll it for the new outcome, the
 * same as after creating a preview.
 */
export function useRefreshSalaryBatch() {
	const queryClient = useQueryClient();

	return useMutation<SalaryBatch, ApiErrorResponse, string>(
		(salaryBatchId) =>
			apiStore
				.post<SalaryBatch>({
					path: `/salary-batch/${salaryBatchId}/refresh`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["salary-batches"]);
				queryClient.invalidateQueries(["salary-batch-staleness"]);
			},
		}
	);
}

/**
 * Whether the time reports behind a draft's preview drifted since it was
 * computed. Only meaningful for a PREVIEW batch, so callers gate on that.
 * A backend without the endpoint (not yet deployed) reads as "not stale".
 */
export function useSalaryBatchStaleness(salaryBatchId?: string, enabled = true) {
	const { data, ...rest } = useQuery(
		["salary-batch-staleness", salaryBatchId],
		() =>
			apiStore
				.get<BatchStaleness>({
					path: `/salary-batch/${salaryBatchId}/staleness`,
					isAuthRequired: true,
				})
				.then((d) => d.data)
				.catch(() => undefined),
		{ enabled: !!salaryBatchId && enabled }
	);

	return { staleness: data, ...rest };
}

/**
 * Resolve the downloadable export-file URL for a processed batch. Only HOGIA
 * has a file today — one zip holding the person file and both salary file
 * formats (FORTNOX is exported via API, EXCEL is unsupported).
 */
export function useSalaryExportFileUrl() {
	return useMutation<string | null, ApiErrorResponse, { type: SalaryBatch["type"]; file: string }>(
		async ({ type, file }) => {
			if (!type || type === "NONE") return null;
			if (type !== "HOGIA") return null;

			const { data } = await apiStore.get<{ url: string }>({
				path: `/salary-batch/hogia/export-url`,
				query: { file },
				isAuthRequired: true,
			});

			return data.url;
		}
	);
}
